import { Check, ChevronDownIcon } from "lucide-react";
import { type KeyboardEvent, type ReactNode, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { Chip } from "@/components/chip";
import { Highlight } from "@/components/highlight";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SELECT_TRIGGER_CLASS } from "@/components/ui/select";
import { matchesSearch } from "@/lib/search";
import { cn } from "@/lib/utils";

/** One choice of a `<SearchMultiSelect>`. */
export interface SearchOption {
  /** What the filter keeps: a tool's name, a person's id, an action's code. */
  value: string;
  /** What people read: a tool's title, a person's name, an action in words. */
  label: string;
  /** A muted second line: a tool's name, a person's email, an action's code. */
  detail?: string;
  /** A small line icon before the label, decorative (`aria-hidden`). */
  icon?: ReactNode;
  /**
   * A muted chip after the label that says what sets the option apart, such
   * as "Niet meer beschikbaar" for a tool DC Data no longer serves.
   */
  note?: string;
}

interface SearchMultiSelectProps {
  options: readonly SearchOption[];
  /** The chosen values; none means every option. */
  value: readonly string[];
  /** The new choice, in the options' order. */
  onChange: (value: string[]) => void;
  /** What is chosen, for screen readers and the list's name: "Tools". */
  name: string;
  /** The button's text while nothing is chosen: "Alle tools". */
  allLabel: string;
  /** The button's text for two or more: "3 tools". One shows its own label. */
  countLabel: (count: number) => string;
  /** The search box's placeholder: "Zoek een tool". */
  searchLabel?: string;
  /** The second lines in small mono, as technical names (a tool's, an action's code). */
  monoDetail?: boolean;
  className?: string;
}

/**
 * A filter on a long list (tools, people, actions) that you can type in, in
 * the dashboard's theme, built like `<PersonPicker>`: a button like
 * `<SelectTrigger>` in the filter bar ("Alle tools", the one chosen, or "3
 * tools") opens a search box over the options, each with its icon, its label
 * and a muted second line (a tool's name), a muted chip where an option
 * stands apart (a tool no longer served), a checkbox, and what was typed
 * marked in both (`<Highlight>`). Typing filters on the label and the second
 * line; the list stays open while ticking. Keyboard: the arrows move, Enter
 * ticks or unticks, Escape closes. "Wissen" clears the choice. A chosen value
 * that is not among the options (a tool from another period, in a shared
 * link) is listed too, so it can be unticked.
 *
 *     <SearchMultiSelect
 *       options={tools.map((tool) => ({ value: tool.name, label: tool.title, detail: tool.name,
 *         icon: <ToolIcon name={tool.name} className="text-primary size-4" /> }))}
 *       value={filters.tools}
 *       onChange={(tools) => setFilters({ ...filters, tools })}
 *       monoDetail
 *       name={t("analytics.tool")}
 *       allLabel={t("analytics.allTools")}
 *       countLabel={(count) => t("filters.toolCount", { count })}
 *     />
 */
export function SearchMultiSelect({
  options,
  value,
  onChange,
  name,
  allLabel,
  countLabel,
  searchLabel,
  monoDetail = false,
  className,
}: SearchMultiSelectProps) {
  const { t } = useTranslation();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  const chosen = new Set(value);
  const known = new Set(options.map((option) => option.value));
  // A chosen value the options lack stays listed, so it can be unticked.
  const all: SearchOption[] = [
    ...options,
    ...value.filter((v) => !known.has(v)).map((v): SearchOption => ({ value: v, label: v })),
  ];
  const shown = all.filter((option) => matchesSearch([option.label, option.detail], query));
  const current = shown.length > 0 ? Math.min(active, shown.length - 1) : null;
  const optionId = (index: number) => `${listId}-${index}`;
  const single = value.length === 1 ? all.find((option) => option.value === value[0]) : undefined;
  const label = value.length === 0 ? allLabel : single ? single.label : countLabel(value.length);

  // Keep the option the arrows reached in view.
  useEffect(() => {
    if (open) {
      document.getElementById(`${listId}-${active}`)?.scrollIntoView({ block: "nearest" });
    }
  }, [active, open, listId]);

  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setQuery("");
      setActive(0);
    }
  };

  const toggle = (option: string) => {
    const on = !chosen.has(option);
    onChange(
      all.filter((o) => (o.value === option ? on : chosen.has(o.value))).map((o) => o.value),
    );
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" && shown.length > 0) {
      event.preventDefault();
      setActive((index) => (index + 1) % shown.length);
    } else if (event.key === "ArrowUp" && shown.length > 0) {
      event.preventDefault();
      setActive((index) => (index - 1 + shown.length) % shown.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const option = current === null ? undefined : shown[current];
      if (option) {
        toggle(option.value);
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-size="sm" className={cn(SELECT_TRIGGER_CLASS, className)}>
          <span className="min-w-0 truncate">
            <span className="sr-only">{name}: </span>
            {label}
          </span>
          <ChevronDownIcon aria-hidden="true" className="text-muted size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 max-w-[calc(100vw-2rem)] min-w-(--radix-popover-trigger-width) p-0"
      >
        <div className="border-b p-2">
          <Input
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={current === null ? undefined : optionId(current)}
            aria-label={searchLabel ?? t("filters.search")}
            placeholder={searchLabel ?? t("filters.search")}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            className="h-9 pointer-coarse:h-10"
          />
        </div>
        <ul
          id={listId}
          role="listbox"
          aria-label={name}
          aria-multiselectable="true"
          className="scrollbar-themed max-h-64 overflow-y-auto p-1"
        >
          {shown.map((option, index) => {
            const on = chosen.has(option.value);
            return (
              <li
                key={option.value}
                id={optionId(index)}
                role="option"
                aria-selected={on}
                // Keep the focus in the search box, so typing goes on after a click.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => toggle(option.value)}
                onMouseMove={() => setActive(index)}
                className={cn(
                  "flex cursor-default items-start gap-2 rounded-sm px-2 py-1.5 text-sm pointer-coarse:py-2.5",
                  index === current && "bg-accent-light",
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "border-input mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-sm border",
                    on && "bg-accent text-accent-foreground border-accent",
                  )}
                >
                  {on && <Check className="size-3.5" />}
                </span>
                {option.icon && (
                  <span aria-hidden="true" className="mt-0.5 flex shrink-0">
                    {option.icon}
                  </span>
                )}
                <span className="min-w-0">
                  <span className="flex items-center gap-2">
                    <span className="text-foreground min-w-0 truncate font-medium">
                      <Highlight text={option.label} query={query} />
                    </span>
                    {option.note && (
                      <Chip tone="neutral" className="shrink-0 font-normal">
                        {option.note}
                      </Chip>
                    )}
                  </span>
                  {option.detail && option.detail !== option.label && (
                    <span
                      className={cn("text-muted block truncate text-xs", monoDetail && "font-mono")}
                    >
                      <Highlight text={option.detail} query={query} />
                    </span>
                  )}
                </span>
              </li>
            );
          })}
          {shown.length === 0 && (
            <li role="presentation" className="text-muted px-2 py-1.5 text-sm">
              {t("filters.empty")}
            </li>
          )}
        </ul>
        {value.length > 0 && (
          <div className="flex justify-end border-t p-1">
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-accent focus-visible:ring-ring rounded-sm px-2 py-1 text-sm font-medium hover:underline focus-visible:ring-2 focus-visible:outline-none pointer-coarse:py-2"
            >
              {t("filters.clear")}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
